import { Component, effect, inject, input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DelegateManagementResource } from '../../../shared/resources/delegate-management.resource';
import { TeamsCallForwardingSettingsFieldConfigResource } from '../../../shared/resources/teams-call-forwarding-settings-field-config.resource';
import { ActivatedRoute } from '@angular/router';
import {
  AssignedCallingDelegate,
  CallingDelegate,
  ForwardingTargetType,
  ForwardingType,
  Microsoft365UserResult,
  MicrosoftTeamsCallingSettings,
  MicrosoftTeamsUserResult,
  TeamsCallingCallPickupGroup,
  UnansweredDelayInSeconds,
  UnansweredTargetType,
  UserPhoto,
} from '../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { combineLatest, forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { Microsoft365UserResource, UserPhotoSearchParams } from '../../../shared/resources/microsoft-365-user.resource';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import { CallingDelegateCardFormComponent } from './calling-delegate-card-form/calling-delegate-card-form.component';
import { TeamsCallHandlingAndForwardingResource } from '../../../shared/resources/teams-call-handling-and-forwarding.resource';
import { filter, map, take } from 'rxjs/operators';
import { isEqual } from 'lodash';
import {
  ForwardingRulesEntity,
  TeamsCallingForwardingRulesFieldComponent,
} from './forwarding-rules-field/teams-calling-forwarding-rules-field.component';
import {
  TeamsCallingUnansweredRulesFieldComponent,
  UnansweredRulesEntity,
} from './unanswered-rules-field/teams-calling-unanswered-rules-field.component';
import { SafeHtml } from '@angular/platform-browser';
import { AboutCardService } from '../../../../shared/services/microsoft/about-card.service';
import { CallPickupGroupsResource } from '../../../shared/resources/call-pickup-groups.resource';
import { Microsoft360ViewContext } from '../../../../shared/contexts/microsoft-360-view.context';
import { MicrosoftTenantsStateContext } from '../../../../shared/contexts/microsoft-tenants-state.context';
import { MicrosoftTeamsUserSearchResource } from '../../../shared/resources/microsoft-teams-user-search.resource';

interface DelegateManagementFormData {
  searchTerm: string | ZiroSelectOption;
  forwardingRules: ForwardingRulesEntity;
  unansweredRules: UnansweredRulesEntity;
}

export interface SelectedDelegate {
  assignedCallingDelegate: AssignedCallingDelegate;
  userPhoto: UserPhoto;
}

export enum UnansweredCallForwardingSettingsTargetType {
  NONE = 'NONE',
  VOICEMAIL = 'VOICEMAIL',
  SINGLE_TARGET_USER = 'SINGLE_TARGET_USER',
  SINGLE_TARGET_NUMBER = 'SINGLE_TARGET_NUMBER',
  DELEGATES = 'DELEGATES',
  CALL_PICKUP_GROUP = 'CALL_PICKUP_GROUP',
}

@Component({
  selector: 'ziro-unanswered-call-forwarding-settings',
  templateUrl: './unanswered-call-forwarding-settings.component.html',
  providers: [
    DelegateManagementResource,
    TeamsCallHandlingAndForwardingResource,
    TeamsCallForwardingSettingsFieldConfigResource,
  ],
  standalone: false,
})
export class UnansweredCallForwardingSettingsComponent
  extends ZiroFormAbstractDirective<DelegateManagementFormData>
  implements OnInit, OnDestroy
{
  private _delegateManagementResource = inject(DelegateManagementResource);
  private _route = inject(ActivatedRoute);
  private _microsoft365UserResource = inject(Microsoft365UserResource);
  private _microsoftTeamsUserSearchResource = inject(MicrosoftTeamsUserSearchResource);
  private _teamsCallHandlingAndForwardingResource = inject(TeamsCallHandlingAndForwardingResource);
  private _teamsCallForwardingSettingsFieldConfigResource = inject(TeamsCallForwardingSettingsFieldConfigResource);
  private _callPickupGroupsResource = inject(CallPickupGroupsResource);
  private _aboutCardService = inject(AboutCardService);
  private _microsoft360ViewContext = inject(Microsoft360ViewContext);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);

  readonly dialPlanGroupId = input<number>();

  @ViewChildren(CallingDelegateCardFormComponent) childForms: QueryList<CallingDelegateCardFormComponent>;
  isLoading = true;

  ForwardingType = ForwardingType;
  userPrincipalName: string;
  userDisplayName: string;
  formConfig: ZiroFormConfig;
  teamsUserResults: MicrosoftTeamsUserResult[];
  userPhotos: UserPhoto[] = [];
  selectedDelegates: SelectedDelegate[] = [];
  infoAlertContent: SafeHtml;
  callPickupGroups: TeamsCallingCallPickupGroup;
  tenantId: string;

  private _initialDelegate: CallingDelegate;
  private _initialTeamsCallingSettings: MicrosoftTeamsCallingSettings;
  private _hasExistingLineUri: boolean;
  private _lastAppliedFieldConfigDialPlanGroupId: number;
  private _subscriptions = new Subscription();

  constructor() {
    super();
    effect(() => {
      const dialPlanGroupId = this.dialPlanGroupId();
      if (
        !dialPlanGroupId ||
        this._hasExistingLineUri ||
        dialPlanGroupId === this._lastAppliedFieldConfigDialPlanGroupId
      ) {
        return;
      }
      this._lastAppliedFieldConfigDialPlanGroupId = dialPlanGroupId;
      const subscription = this._teamsCallForwardingSettingsFieldConfigResource
        .get(this.tenantId, dialPlanGroupId)
        .subscribe((fieldConfig) => {
          this.entitySource.next({
            ...this.entity,
            unansweredRules: {
              ...this.entity.unansweredRules,
              unansweredTargetType: fieldConfig.unansweredTargetType,
              unansweredDelayInSeconds: fieldConfig.unansweredDelayInSeconds,
            },
          });
        });
      this._subscriptions.add(subscription);
    });
  }

  ngOnInit(): void {
    this.userPrincipalName = this._route.snapshot.paramMap.get('upn');
    this._initFormConfig();
    combineLatest([
      this._tenantsStateContext.cachedTenantState$.pipe(filter((state) => !!state?.tenantId)),
      this._microsoft360ViewContext.state$,
    ])
      .pipe(
        take(1),
        switchMap(([cachedTenantState, ms360View]) => {
          this.tenantId = cachedTenantState.tenantId.toString();
          const hasExistingLineUri = !!ms360View.teamsPhoneNumberAssignment?.lineUri;
          this._hasExistingLineUri = hasExistingLineUri;
          return this._initUser(hasExistingLineUri);
        }),
      )
      .subscribe(() => {
        this.isLoading = false;
      });

    const updateSub = this.ziroFormsUpdate$.subscribe((update) => {
      const selectedOption = update.new.searchTerm as ZiroSelectOption;
      if (selectedOption) {
        const selectedOptionUpn = selectedOption.value as string;
        const displayName = selectedOption.label;
        this._addSelectedDelegate(selectedOptionUpn, displayName);
        this.entitySource.next({ ...update.new, searchTerm: undefined });
      }
    });
    this._subscriptions.add(updateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  delegateRemoveClicked(delegateId: string): void {
    this._ziroFormStateService.setIsFormDirty(true);
    this.selectedDelegates = this.selectedDelegates.filter(
      (delegate) => delegate.assignedCallingDelegate.id !== delegateId,
    );
  }

  getSelectedDelegates(): AssignedCallingDelegate[] {
    return this.selectedDelegates.map((delegate) => delegate.assignedCallingDelegate);
  }

  updateSelectedDelegate($event: ZiroFormsUpdate<AssignedCallingDelegate>): void {
    const selectedDelegate = this.selectedDelegates.find(
      (delegate) => delegate.assignedCallingDelegate.id === $event.new.id,
    );
    selectedDelegate.assignedCallingDelegate = $event.new;
  }

  validateForm(): void {
    this._validateAndSubmitSource.next(true);
  }

  save(): Observable<void> {
    return this._saveCallingDelegate().pipe(switchMap(() => this._updateTeamsCallingSettings()));
  }

  delete(): Observable<void> {
    return forkJoin([this._removeDelegates(), this._clearForwardingRules()]).pipe(
      map(() => {
        this._ziroFormStateService.setIsFormDirty(false);
      }),
    );
  }

  protected submit(): Observable<void> {
    return of(null);
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        searchTerm: {
          label: 'tkey;microsoft_360.view.delegate_management.add_delegate.label',
          dataAutomation: 'async-user-search',
          componentConfig: new ZiroSelectConfig({
            options: [],
            isMultiSelect: false,
            asyncOptionsFn: (searchTerm) => this._getUserAsync(searchTerm),
            showAutoGenerationLink: true,
            placeholder: 'tkey;microsoft_360.view.delegate_management.add_delegate.placeholder',
            minSearchLength: 3,
            clearWithInput: true,
          }),
        },
        forwardingRules: {
          validation: [
            {
              validator: () => {
                const forwardingRulesComponent = this.fieldComponents.find(
                  (field) => field.fieldId() === 'forwardingRules',
                ) as TeamsCallingForwardingRulesFieldComponent;
                return forwardingRulesComponent.hasValidationError
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
            },
          ],
        },
        unansweredRules: {
          validation: [
            {
              validator: () => {
                const unansweredRulesComponent = this.fieldComponents.find(
                  (field) => field.fieldId() === 'unansweredRules',
                ) as TeamsCallingUnansweredRulesFieldComponent;
                return unansweredRulesComponent.hasValidationError
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
            },
          ],
          valExcluded: () => this.formData?.forwardingRules?.forwardingType === ForwardingType.IMMEDIATE,
        },
      },
      options: {
        columnClasses: {
          label: 'ms-4 ps-4 w-auto',
          input: 'w-75',
        },
      },
    };
  }

  private _setCurrentUserData(): Observable<Microsoft365UserResult[]> {
    return this._microsoft365UserResource.search(this.tenantId, this.userPrincipalName).pipe(
      tap((results) => {
        this.userDisplayName = results[0].microsoft365UserRef.displayName;
      }),
    );
  }

  private _setCallingDelegate(): Observable<UserPhoto[]> {
    return this._delegateManagementResource.getCallingDelegate(this.tenantId, this.userPrincipalName).pipe(
      switchMap((delegate: CallingDelegate) => {
        this._initialDelegate = delegate;
        const userPhotoSearchParams: UserPhotoSearchParams = {
          photoSize: '48x48',
          userPrincipalNames: delegate?.assignedCallingDelegates.map((assignedDelegate) => assignedDelegate.id),
        };
        return userPhotoSearchParams?.userPrincipalNames?.length
          ? this._microsoft365UserResource.userPhotoSearch(this.tenantId, userPhotoSearchParams)
          : of(null);
      }),
      tap((userPhotos: UserPhoto[]) => {
        if (this._initialDelegate?.assignedCallingDelegates?.length) {
          this.userPhotos = this.userPhotos.concat(userPhotos);
          this.selectedDelegates = this._initialDelegate.assignedCallingDelegates.map((assignedDelegate) => {
            return {
              assignedCallingDelegate: assignedDelegate,
              userPhoto: userPhotos.find((userPhoto) => userPhoto.userPrincipalName === assignedDelegate.id),
            };
          });
        }
      }),
    );
  }

  private _setInfoAlertContent(): Observable<void> {
    return this._aboutCardService.getAboutCard(65).pipe(
      map((content) => {
        this.infoAlertContent = content;
      }),
    );
  }

  private _setCallPickupGroups(): Observable<void> {
    return this._callPickupGroupsResource.get(this.tenantId, this.userPrincipalName).pipe(
      map((callPickupGroups) => {
        this.callPickupGroups = callPickupGroups;
      }),
    );
  }

  private _setCallForwardingAndUnansweredCallRules(): Observable<MicrosoftTeamsCallingSettings> {
    return this._teamsCallHandlingAndForwardingResource.getTeamsCallingHandlingForwarding(
      this.tenantId,
      this.userPrincipalName,
    );
  }

  private _initUser(hasExistingLineUri: boolean): Observable<void> {
    return forkJoin([
      this._setCallForwardingAndUnansweredCallRules(),
      this._setCurrentUserData(),
      this._setCallingDelegate(),
      this._setInfoAlertContent(),
      this._setCallPickupGroups(),
    ]).pipe(
      map(([teamsCallingSettings]) => {
        this._initialTeamsCallingSettings = teamsCallingSettings;
        const shouldApplyVoicemailDefault = !hasExistingLineUri;
        this.entitySource.next({
          searchTerm: undefined,
          forwardingRules: {
            forwardingType: teamsCallingSettings.forwardingType,
            forwardingTargetType: teamsCallingSettings.forwardingTargetType,
            forwardingTarget: teamsCallingSettings.forwardingTarget,
          },
          unansweredRules: {
            unansweredTargetType: shouldApplyVoicemailDefault
              ? UnansweredTargetType.VOICEMAIL
              : teamsCallingSettings.unansweredTargetType,
            unansweredTarget: teamsCallingSettings.unansweredTarget,
            unansweredDelayInSeconds: shouldApplyVoicemailDefault
              ? UnansweredDelayInSeconds.SECONDS_20
              : teamsCallingSettings.unansweredDelayInSeconds,
          },
        });
      }),
    );
  }

  private _getUserAsync(searchTerm: string): Observable<ZiroSelectOption[]> {
    return this._microsoftTeamsUserSearchResource.search(this.tenantId, searchTerm).pipe(
      switchMap((results: MicrosoftTeamsUserResult[]) => {
        this.teamsUserResults = results.filter(
          (result) =>
            !this.selectedDelegates.some(
              (delegate) => delegate.assignedCallingDelegate.id === result.userPrincipalName,
            ) && result.userPrincipalName !== this.userPrincipalName,
        );
        return results.length
          ? this._microsoft365UserResource.userPhotoSearch(this.tenantId, {
              photoSize: '48x48',
              userPrincipalNames: results.map((result) => result.userPrincipalName),
            })
          : of([]);
      }),
      map((userPhotos: UserPhoto[]) => {
        this.userPhotos = userPhotos;
        return this.teamsUserResults.map((result) => {
          const userPhoto =
            userPhotos.find((userPhoto) => userPhoto.userPrincipalName === result.userPrincipalName)?.photoBase64 || '';
          return {
            label: result.ref.displayName,
            value: result.userPrincipalName,
            photoBase64: userPhoto,
            displayIcon: !userPhoto,
            disabled: !result.enterpriseVoiceEnabled,
            appendValue: true,
            disabledTooltip: !result.enterpriseVoiceEnabled
              ? 'tkey;microsoft_360.view.delegate_management.add_delegate.disabled_tooltip'
              : '',
            tooltipPlacement: 'start',
          };
        });
      }),
    );
  }

  private _addSelectedDelegate(upn: string, displayName: string): void {
    const selectedDelegate: SelectedDelegate = {
      assignedCallingDelegate: {
        id: upn,
        makeCallsEnabled: true,
        receiveCallsEnabled: true,
        manageSettingsEnabled: false,
      },
      userPhoto: {
        userPrincipalName: upn,
        displayName,
        photoBase64: this.userPhotos.find((userPhoto) => userPhoto.userPrincipalName === upn)?.photoBase64 || '',
      },
    };
    this.selectedDelegates.push(selectedDelegate);
  }

  private _saveCallingDelegate(): Observable<void> {
    const callingDelegate: CallingDelegate = {
      userPrincipalName: this.userPrincipalName,
      assignedCallingDelegates: this.childForms.map((assignedDelegate) => assignedDelegate.entity),
    };
    return !isEqual(callingDelegate, this._initialDelegate)
      ? this._delegateManagementResource.putCallingDelegate(this.tenantId, callingDelegate)
      : of(null);
  }

  private _updateTeamsCallingSettings(): Observable<void> {
    const currentSettings: MicrosoftTeamsCallingSettings = {
      userPrincipalName: this.userPrincipalName,
      ...this.entity.forwardingRules,
      ...this.entity.unansweredRules,
    };

    if (currentSettings.forwardingType === ForwardingType.IMMEDIATE) {
      currentSettings.unansweredTargetType = UnansweredTargetType.NONE;
      currentSettings.unansweredTarget = '';
      currentSettings.unansweredDelayInSeconds = UnansweredDelayInSeconds.NONE;
    }

    return !isEqual(currentSettings, this._initialTeamsCallingSettings)
      ? this._teamsCallHandlingAndForwardingResource.putTeamsCallingHandlingForwarding(this.tenantId, currentSettings)
      : of(null);
  }

  private _removeDelegates(): Observable<void> {
    if (this._initialDelegate?.assignedCallingDelegates.length > 0) {
      return this._delegateManagementResource.putCallingDelegate(this.tenantId, {
        userPrincipalName: this.userPrincipalName,
        assignedCallingDelegates: [],
      });
    }
    return of(null);
  }

  private _clearForwardingRules(): Observable<void> {
    const emptyRules: MicrosoftTeamsCallingSettings = {
      userPrincipalName: this.userPrincipalName,
      forwardingType: ForwardingType.NONE,
      forwardingTargetType: ForwardingTargetType.NONE,
      forwardingTarget: '',
      unansweredTargetType: UnansweredTargetType.NONE,
      unansweredTarget: '',
      unansweredDelayInSeconds: UnansweredDelayInSeconds.NONE,
    };
    if (!isEqual(emptyRules, this._initialTeamsCallingSettings)) {
      return this._teamsCallHandlingAndForwardingResource.putTeamsCallingHandlingForwarding(this.tenantId, emptyRules);
    }
    return of(null);
  }
}
